import { useBehavior } from '@libs/core';
import { TWidget } from '@libs/types';
import React, { MouseEventHandler } from 'react';
import { TVisualData } from '../../types/data';
import { TVisualOption } from '../../types/options';
import { VisualView } from './VisualView';

type TVisualWidgetProps = {
    widget: TWidget<TVisualData, TVisualOption>;
    handleClick?: MouseEventHandler;
};

/**
 * Standalone Visual widget driven entirely by server-provided configuration.
 * Owns the useBehavior hook and delegates all rendering to VisualView.
 *
 * Use this component when the widget definition comes from the server and no
 * additional prop overrides are required. For composable usage (e.g. inside
 * complex consumer widgets), use VisualView directly with explicit props.
 */
export const VisualWidget = ({ widget, handleClick }: TVisualWidgetProps) => {
    const { isDisabled, isHidden } = useBehavior(widget);

    const { iconName, defaultIconName, flagIcon, flagIconSmall, base64, avatar } = widget.data ?? {};
    const { margin, size, iconHierarchy, iconVariant, isSelected, iconColor, backgroundColor } = widget.options ?? {};

    return (
        <VisualView
            uid={widget.uid}
            iconName={iconName}
            defaultIconName={defaultIconName}
            flagIcon={flagIcon}
            flagIconSmall={flagIconSmall}
            base64={base64}
            avatar={avatar}
            margin={margin}
            size={size}
            iconHierarchy={iconHierarchy}
            iconVariant={iconVariant}
            iconColor={iconColor}
            backgroundColor={backgroundColor}
            isSelected={isSelected}
            isDisabled={isDisabled}
            isHide={isHidden}
            parentStyleNames={[widget.options?.style].filter(Boolean) as string[]}
            handleClick={handleClick}
        />
    );
};
